import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { map, Observable, switchMap, tap, withLatestFrom } from 'rxjs';

import { updatingSortActivityModeInMultiUserPreferences } from 'app/state/multi-user-preferences/multi-user-preferences.actions';
import { selectMultiUserPreferencesIfLoaded } from 'app/state/multi-user-preferences/multi-user-preferences.selectors';
import { selectAppModeIfDefined } from 'app/state/settings/settings.selectors';
import { updatingSortActivityModeInPersonalUserPreferences } from 'app/state/user-session/user-session.actions';
import { selectPersonalUserPreferences } from 'app/state/user-session/user-session.selectors';

import { defaultSortActivityMode, SortActivityMode } from '../../../../models';
import { Activity } from '../../models';

export interface ActivityFilterState {
    activities: Activity[];
    sortMode: SortActivityMode | undefined;
}

const initialState: ActivityFilterState = { activities: [], sortMode: undefined };

@Injectable()
export class ActivityFilterStore extends ComponentStore<ActivityFilterState> {
    private store = inject(Store);

    constructor() {
        super(initialState);
        this.syncSortModeFromGlobalStore();
    }

    activities$ = this.select((state) => state.activities);
    sortMode$ = this.select((state) => state.sortMode);

    sortActivityModeFromGlobalStore$: Observable<SortActivityMode> = this.store
        .pipe(selectAppModeIfDefined)
        .pipe(
            switchMap((appMode) => {
                return appMode === 'personal'
                    ? this.store.select(selectPersonalUserPreferences)
                    : this.store.pipe(selectMultiUserPreferencesIfLoaded);
            }),
            map(
                (preferences: { sortActivityMode: SortActivityMode } | undefined) =>
                    preferences?.sortActivityMode ?? defaultSortActivityMode,
            ),
        );

    updateActivities = this.updater((state, activities: Activity[]) => ({ ...state, activities }));
    private updateSortMode = this.updater((state, sortMode: SortActivityMode) => ({ ...state, sortMode }));

    sortModeUpdating = this.effect((sortMode$: Observable<SortActivityMode>) =>
        sortMode$.pipe(
            withLatestFrom(this.store.pipe(selectAppModeIfDefined)),
            tap(([sortActivityMode, appMode]) => {
                if (appMode === 'personal') {
                    this.store.dispatch(
                        updatingSortActivityModeInPersonalUserPreferences({ sortActivityMode }),
                    );
                } else {
                    this.store.dispatch(updatingSortActivityModeInMultiUserPreferences({ sortActivityMode }));
                }
            }),
        ),
    );

    syncSortModeFromGlobalStore = this.effect((trigger$) =>
        trigger$.pipe(
            switchMap(() => this.sortActivityModeFromGlobalStore$),
            tap((sortActivityMode) => this.updateSortMode(sortActivityMode)),
        ),
    );
}
